// Centralized localStorage quota management. localStorage is a tiny (~5MB),
// synchronous store. It must only hold small, bounded data. Bulk/retained data
// belongs server-side (chat: chat_messages, capped 100/pair) or on disk
// (durable saves: /api/sessions). This module bounds the redundant renderer
// caches and recovers from quota errors instead of swallowing them silently,
// which was masking save failures and exhausting the quota.

const GAME_STATE_PREFIX = 'valkenhall-game-state-';
const GAME_STATE_KEEP = 3; // durable saves live on disk; these are redundant caches

// Drop redundant/oversized client caches whose authoritative copy lives
// server-side or on disk. Safe. No retention is lost.
export function pruneLocalStorage() {
  try {
    const gameKeys = Object.keys(localStorage).filter((k) => k.startsWith(GAME_STATE_PREFIX));
    if (gameKeys.length > GAME_STATE_KEEP) {
      const scored = gameKeys
        .map((k) => {
          let savedAt = 0;
          try {
            const v = JSON.parse(localStorage.getItem(k));
            savedAt = v?.savedAt ? new Date(v.savedAt).getTime() : 0;
          } catch { /* unparseable. Treat as oldest */ }
          return { k, savedAt };
        })
        .sort((a, b) => b.savedAt - a.savedAt);
      for (const { k } of scored.slice(GAME_STATE_KEEP)) {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      }
    }
  } catch (err) {
    console.warn('[localStorage] prune failed:', err?.message);
  }
}

// setItem that recovers from a full quota by pruning and retrying once, then
// surfaces the failure (never a silent swallow). Returns whether it persisted.
export function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (err?.name === 'QuotaExceededError') {
      pruneLocalStorage();
      try {
        localStorage.setItem(key, value);
        return true;
      } catch {
        console.warn(`[localStorage] quota still exceeded for ${key} after prune (${Math.round((value?.length || 0) / 1024)}KB). Not persisted`);
        return false;
      }
    }
    console.warn(`[localStorage] setItem failed for ${key}:`, err?.message);
    return false;
  }
}
